<template>
  <div class="hub-detail-page" :class="[categoryLayoutClass, layoutProfile.layoutClass]">
    <div class="hub-detail-inner" v-if="article">
      <div class="hub-detail-breadcrumb">
        <router-link :to="homePath">{{ hubText('home') }}</router-link>
        <span>/</span>
        <router-link :to="hubPath">{{ hubText('blog') }}</router-link>
        <span>/</span>
        <router-link :to="categoryPath(article.category)">{{ categoryLabel(article.category) }}</router-link>
        <span>/</span>
        <span>{{ article.title }}</span>
      </div>

      <article class="hub-article-layout">
        <div class="hub-article-main">
          <header class="hub-article-header">
            <div class="hub-article-meta">
              <span v-if="article.badge === 'Hot'" class="hub-meta-hot" aria-label="Hot">
                <span class="hub-meta-hot-icon" aria-hidden="true">🔥</span>
                <span>Hot</span>
              </span>
              <span class="hub-meta-source">Miral AI Blog</span>
              <span class="hub-meta-sep" aria-hidden="true">·</span>
              <router-link :to="categoryPath(article.category)" class="hub-meta-category">{{ categoryLabel(article.category) }}</router-link>
            </div>
            <h1>{{ article.title }}</h1>
            <p class="hub-lead">{{ article.excerpt }}</p>
            <p v-if="layoutProfile.headerHint" class="hub-header-hint">{{ layoutProfile.headerHint }}</p>
            <figure v-if="layoutProfile.showHeroImage && heroImageUrl" class="hub-header-hero">
              <img :src="heroImageUrl" :alt="article.title" loading="eager">
            </figure>
            <div v-if="layoutProfile.showComparePills" class="hub-compare-pills">
              <span v-for="item in comparePills" :key="'pill-' + item">{{ item }}</span>
            </div>
            <div v-if="showWorkflowStrip" class="hub-workflow-strip">
              <template v-for="(item, index) in workflowSteps">
                <span :key="'workflow-' + item">{{ item }}</span>
                <span v-if="index < workflowSteps.length - 1" :key="'workflow-arrow-' + item" class="hub-workflow-arrow">→</span>
              </template>
            </div>
            <p v-if="showEditorNote" class="hub-editor-note">{{ editorNote }}</p>
            <div class="hub-hero-cta">
              <router-link :to="localizedPath(article.target.path)" class="hub-primary-cta">{{ freeTrialLabel(article.target.label) }}</router-link>
              <router-link :to="hubPath" class="hub-secondary-cta">{{ hubText('viewAllBlog') }}</router-link>
            </div>
          </header>

          <div class="hub-article-body">
          <nav
            v-if="inArticleNavTree.length"
            class="hub-in-article-nav"
            :class="{ 'is-expanded': inArticleNavOpen }"
            :aria-label="hubText('inArticleLabel')"
          >
            <button
              type="button"
              class="hub-in-article-toggle"
              :aria-expanded="inArticleNavOpen ? 'true' : 'false'"
              @click="toggleInArticleNav"
            >
              <span class="hub-in-article-toggle-left">
                <span class="hub-in-article-menu-icon" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
                <span class="hub-in-article-label">{{ layoutProfile.navLabel }}</span>
              </span>
              <span class="hub-in-article-chevron" :class="{ 'is-open': inArticleNavOpen }"></span>
            </button>
            <div v-show="inArticleNavOpen" class="hub-in-article-scroll">
              <ul class="hub-in-article-list">
                <li
                  v-for="item in inArticleNavTree"
                  :key="'in-' + item.id"
                  class="hub-in-article-item"
                  :class="{ 'has-children': item.children.length }"
                >
                  <div class="hub-in-article-row">
                    <span class="hub-in-article-dot" aria-hidden="true"></span>
                    <a :href="'#' + item.id" class="hub-in-article-text">{{ item.title }}</a>
                    <button
                      v-if="item.children.length"
                      type="button"
                      class="hub-in-article-sub-toggle"
                      :aria-expanded="isNavSectionOpen(item.id) ? 'true' : 'false'"
                      :aria-label="hubText('expandPrefix') + item.title"
                      @click.stop="toggleNavSection(item.id)"
                    >
                      <span class="hub-in-article-chevron hub-in-article-chevron-sm" :class="{ 'is-open': isNavSectionOpen(item.id) }"></span>
                    </button>
                  </div>
                  <ul v-if="item.children.length && isNavSectionOpen(item.id)" class="hub-in-article-sub">
                    <li v-for="child in item.children" :key="'in-c-' + child.id">
                      <a :href="'#' + child.anchor">{{ child.title }}</a>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </nav>

          <div v-if="quickAnchorItems.length" class="hub-quick-anchors" :aria-label="hubText('quickAnchorLabel')">
            <span>{{ hubText('quickJump') }}</span>
            <a
              v-for="item in quickAnchorItems"
              :key="'quick-' + item.id"
              :href="'#' + item.id"
            >{{ item.title }}</a>
          </div>

          <template v-for="section in article.sections">
            <section
              :key="section.id"
              :id="section.id"
              class="hub-article-section"
              :class="{
                'hub-section-tldr': section.id === 'tldr',
                'hub-section-faq': section.id === 'faq',
                'hub-section-conclusion': section.id === 'conclusion',
                'hub-section-platform-ref': section.id === 'platform-ref',
                'hub-section-intents': section.id === 'search-intents',
                'hub-section-prompts': section.id === 'prompt-examples'
              }"
            >
              <div v-if="section.id === 'tldr'" class="hub-tldr-module">
                <div class="hub-module-head">
                  <span>{{ layoutProfile.tldrLabel }}</span>
                  <h2>{{ layoutProfile.tldrTitle }}</h2>
                  <p v-if="layoutProfile.tldrDesc">{{ layoutProfile.tldrDesc }}</p>
                </div>
                <template v-for="(block, index) in section.blocks">
                  <p v-if="block.type === 'paragraph'" :key="'tl-p-' + index" v-html="renderInlineText(block.text)"></p>
                  <ul v-else-if="block.type === 'bullets'" :key="'tl-b-' + index" class="hub-bullet-list">
                    <li v-for="item in block.items" :key="item">{{ item }}</li>
                  </ul>
                  <div v-else-if="block.type === 'table'" :key="'tl-t-' + index" class="hub-table-block">
                    <div class="hub-theme-box" :class="{ 'hub-theme-overview': block.variant === 'overview' }">
                      <div class="hub-table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th v-for="col in block.columns" :key="col">{{ col }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                              <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
              <div v-else-if="section.id === 'conclusion'" class="hub-conclusion-module">
                <div class="hub-module-head">
                  <span>{{ layoutProfile.conclusionLabel }}</span>
                  <h2>{{ layoutProfile.conclusionTitle }}</h2>
                </div>
                <template v-for="(block, index) in section.blocks">
                  <p v-if="block.type === 'paragraph'" :key="'cl-p-' + index" v-html="renderInlineText(block.text)"></p>
                  <ul v-else-if="block.type === 'bullets'" :key="'cl-b-' + index" class="hub-bullet-list hub-conclusion-list">
                    <li v-for="item in block.items" :key="item">{{ item }}</li>
                  </ul>
                  <div v-else-if="block.type === 'cta'" :key="'cl-c-' + index" class="hub-theme-box hub-inline-cta hub-conclusion-cta">
                    <div>
                      <strong>{{ block.title }}</strong>
                      <p>{{ block.text }}</p>
                    </div>
                    <router-link v-if="block.buttonPath" :to="localizedPath(block.buttonPath)">{{ ctaButtonLabel(block) }}</router-link>
                  </div>
                </template>
              </div>
              <div v-else-if="section.id === 'search-intents'" class="hub-intents-module">
                <div class="hub-module-head">
                  <span>{{ layoutProfile.intentsLabel }}</span>
                  <h2>{{ section.title }}</h2>
                </div>
                <div
                  class="hub-intents-panel hub-collapsible-panel"
                  :class="{ 'is-hub-collapsed': shouldLimitSearchIntents(section) && !isCollapsibleExpanded(section.id) }"
                >
                  <div
                    :id="'hub-intents-list-' + section.id"
                    class="hub-intents-list"
                  >
                    <template v-if="!shouldLimitSearchIntents(section)">
                      <template v-for="(block, index) in section.blocks">
                        <p
                          v-if="block.type === 'paragraph'"
                          :key="'in-p-' + index"
                          class="hub-intents-desc"
                          v-html="renderInlineText(block.text)"
                        ></p>
                        <div
                          v-else-if="block.type === 'scenario-item'"
                          :key="'in-s-' + index"
                          :id="section.id + '-s-' + index"
                          class="hub-intent-card"
                        >
                          <router-link :to="hubSearchRoute(block.title)" class="hub-intent-title">{{ block.title }}</router-link>
                          <p v-html="renderInlineText(block.text)"></p>
                        </div>
                      </template>
                    </template>
                    <template v-else>
                      <template v-for="entry in searchIntentPrimaryEntries(section)">
                        <p
                          v-if="entry.block.type === 'paragraph'"
                          :key="'in-p-' + entry.index"
                          class="hub-intents-desc"
                        v-html="renderInlineText(entry.block.text)"
                        ></p>
                        <div
                          v-else-if="entry.block.type === 'scenario-item'"
                          :key="'in-s-' + entry.index"
                          :id="section.id + '-s-' + entry.index"
                          class="hub-intent-card"
                        >
                          <router-link :to="hubSearchRoute(entry.block.title)" class="hub-intent-title">{{ entry.block.title }}</router-link>
                          <p v-html="renderInlineText(entry.block.text)"></p>
                        </div>
                      </template>
                      <div
                        v-show="isCollapsibleExpanded(section.id)"
                        :id="'hub-intents-extra-' + section.id"
                        class="hub-intents-extra"
                      >
                        <div
                          v-for="entry in searchIntentExtraEntries(section)"
                          :key="'in-ex-' + entry.index"
                          :id="section.id + '-s-' + entry.index"
                          class="hub-intent-card"
                        >
                          <router-link :to="hubSearchRoute(entry.block.title)" class="hub-intent-title">{{ entry.block.title }}</router-link>
                          <p v-html="renderInlineText(entry.block.text)"></p>
                        </div>
                      </div>
                      <div class="hub-collapsible-more-wrap hub-intents-more-wrap">
                        <button
                          type="button"
                          class="hub-collapsible-more"
                          :class="{ 'is-expanded': isCollapsibleExpanded(section.id) }"
                          :aria-expanded="isCollapsibleExpanded(section.id) ? 'true' : 'false'"
                          :aria-controls="'hub-intents-extra-' + section.id"
                          @click="toggleCollapsibleSection(section.id, 'hub-intents-list-')"
                        >
                          <span class="hub-collapsible-more-icon" aria-hidden="true"></span>
                          <span class="hub-collapsible-more-text">{{ collapsibleToggleLabel(section, 'scenario-item') }}</span>
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div v-else-if="section.id === 'faq'" class="hub-faq-module">
                <div class="hub-module-head">
                  <span>{{ layoutProfile.faqLabel }}</span>
                  <h2>{{ layoutProfile.faqTitle }}</h2>
                </div>
                <div
                  class="hub-faq-panel hub-collapsible-panel"
                  :class="{ 'is-hub-collapsed': shouldLimitFaq(section) && !isCollapsibleExpanded(section.id) }"
                >
                  <div
                    :id="'hub-faq-list-' + section.id"
                    class="hub-faq-list hub-theme-box hub-faq-theme"
                  >
                    <template v-if="!shouldLimitFaq(section)">
                      <div
                        v-for="(block, index) in section.blocks"
                        v-if="block.type === 'faq-item'"
                        :key="'faq-' + index"
                        :id="section.id + '-q-' + index"
                        class="hub-faq-entry"
                      >
                        <h3>{{ block.question }}</h3>
                        <p v-html="renderInlineText(block.answer)"></p>
                      </div>
                    </template>
                    <template v-else>
                      <div
                        v-for="entry in faqPrimaryEntries(section)"
                        :key="'faq-p-' + entry.index"
                        :id="section.id + '-q-' + entry.index"
                        class="hub-faq-entry"
                      >
                        <h3>{{ entry.block.question }}</h3>
                        <p v-html="renderInlineText(entry.block.answer)"></p>
                      </div>
                      <div
                        v-show="isCollapsibleExpanded(section.id)"
                        :id="'hub-faq-extra-' + section.id"
                        class="hub-faq-extra"
                      >
                        <div
                          v-for="entry in faqExtraEntries(section)"
                          :key="'faq-ex-' + entry.index"
                          :id="section.id + '-q-' + entry.index"
                          class="hub-faq-entry"
                        >
                          <h3>{{ entry.block.question }}</h3>
                          <p v-html="renderInlineText(entry.block.answer)"></p>
                        </div>
                      </div>
                      <div class="hub-collapsible-more-wrap hub-faq-more-wrap">
                        <button
                          type="button"
                          class="hub-collapsible-more"
                          :class="{ 'is-expanded': isCollapsibleExpanded(section.id) }"
                          :aria-expanded="isCollapsibleExpanded(section.id) ? 'true' : 'false'"
                          :aria-controls="'hub-faq-extra-' + section.id"
                          @click="toggleCollapsibleSection(section.id, 'hub-faq-list-')"
                        >
                          <span class="hub-collapsible-more-icon" aria-hidden="true"></span>
                          <span class="hub-collapsible-more-text">{{ collapsibleToggleLabel(section, 'faq-item') }}</span>
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div v-else-if="section.id === 'prompt-examples'" class="hub-prompt-stack">
                <h2>{{ section.title }}</h2>
                <div
                  v-for="(block, index) in section.blocks"
                  v-if="block.type === 'prompt-example'"
                  :key="'pe-' + index"
                  :id="section.id + '-p-' + index"
                  class="hub-prompt-stack-item"
                >
                  <h3 class="hub-prompt-stack-label">{{ block.label || hubText('promptExample') }}</h3>
                  <figure v-if="block.imageKey" class="hub-media-figure hub-prompt-stack-media">
                    <img :src="resolveHubImage(block.imageKey)" :alt="block.label" loading="lazy">
                  </figure>
                  <p class="hub-prompt-stack-text">{{ block.prompt }}</p>
                  <button type="button" class="hub-copy-btn" @click="copyPrompt(block.prompt, $event)">{{ hubText('copyPrompt') }}</button>
                </div>
              </div>
              <template v-else>
                <h2>{{ section.title }}</h2>
                <template v-for="(block, index) in section.blocks">
                <p v-if="block.type === 'paragraph'" :key="index" v-html="renderInlineText(block.text)"></p>
                <ul v-else-if="block.type === 'bullets'" :key="index" class="hub-bullet-list">
                  <li v-for="item in block.items" :key="item">{{ item }}</li>
                </ul>
                <div v-else-if="block.type === 'prompts'" :key="index" class="hub-prompt-text-list">
                  <div v-for="(item, pi) in block.items" :key="'pt-' + pi" class="hub-prompt-text-card">
                    <span class="hub-prompt-label">{{ hubText('promptExample') }}</span>
                    <p class="hub-prompt-text">{{ item }}</p>
                    <button type="button" class="hub-copy-btn" @click="copyPrompt(item, $event)">{{ hubText('copyPrompt') }}</button>
                  </div>
                </div>
                <figure v-else-if="block.type === 'figure'" :key="index" class="hub-media-figure">
                  <img :src="resolveHubImage(block.imageKey)" :alt="block.alt || section.title" loading="lazy">
                  <figcaption v-if="block.caption">{{ block.caption }}</figcaption>
                </figure>
                <figure v-else-if="block.type === 'article-image'" :key="index" class="hub-media-figure hub-article-image">
                  <img :src="resolveHubImage(block.src)" :alt="block.alt || section.title" loading="lazy">
                  <figcaption v-if="block.caption">{{ block.caption }}</figcaption>
                </figure>
                <div
            v-else-if="block.type === 'gallery'"
                  :key="index"
                  class="hub-media-gallery"
                  :class="[
                    'hub-media-gallery-' + (block.fit || 'cover'),
                    'hub-media-gallery-' + (block.ratio || 'square')
                  ]"
                >
                  <div class="hub-media-grid" :class="'hub-media-cols-' + (block.cols || 2)">
                    <figure v-for="(item, gi) in block.items" :key="'g-' + gi" class="hub-media-item">
                      <img :src="resolveHubImage(item.imageKey)" :alt="block.caption || section.title" loading="lazy">
                    </figure>
                  </div>
                  <p v-if="block.caption" class="hub-media-caption">{{ block.caption }}</p>
                </div>
                <div v-else-if="block.type === 'prompt-example' && section.id !== 'prompt-examples'" :key="index" :id="section.id + '-p-inline-' + index" class="hub-prompt-stack-item hub-prompt-stack-item-inline">
                  <h3 class="hub-prompt-stack-label">{{ block.label || hubText('promptExample') }}</h3>
                  <figure v-if="block.imageKey" class="hub-media-figure hub-prompt-stack-media">
                    <img :src="resolveHubImage(block.imageKey)" :alt="block.label" loading="lazy">
                  </figure>
                  <p class="hub-prompt-stack-text">{{ block.prompt }}</p>
                  <button type="button" class="hub-copy-btn" @click="copyPrompt(block.prompt, $event)">{{ hubText('copyPrompt') }}</button>
                </div>
                <div v-else-if="block.type === 'tip'" :key="index" class="hub-theme-box hub-tip-info">
                  <strong>{{ block.title }}</strong>
                  <p v-html="renderInlineText(block.text)"></p>
                </div>
                <div v-else-if="block.type === 'steps'" :key="index" :id="section.id + '-steps'" class="hub-steps-wrap">
                  <h3 class="hub-steps-title">{{ stepsBlockTitle }}</h3>
                  <div class="hub-creating-steps hub-creating-steps-inline">
                    <div v-for="(step, stepIndex) in creatingSteps" :key="'step-' + stepIndex" class="hub-creating-step">
                      <span>{{ stepIndex + 1 }}</span>
                      <strong>{{ step.title }}</strong>
                      <p v-html="renderInlineText(step.text)"></p>
                    </div>
                  </div>
                </div>
                <div v-else-if="block.type === 'pros-cons'" :key="index" class="hub-theme-box hub-pros-cons-theme">
                  <table class="hub-pros-cons-table">
                    <thead>
                      <tr>
                        <th>{{ hubText('pros') }}</th>
                        <th>{{ hubText('cons') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, pcIndex) in prosConsRows" :key="'pc-' + pcIndex">
                        <td>{{ row.pro || '—' }}</td>
                        <td>{{ row.con || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else-if="block.type === 'cta'" :key="index" class="hub-theme-box hub-inline-cta">
                  <div>
                    <strong>{{ block.title }}</strong>
                    <p>{{ block.text }}</p>
                  </div>
                  <router-link v-if="block.buttonPath" :to="localizedPath(block.buttonPath)">{{ ctaButtonLabel(block) }}</router-link>
                </div>
                <div v-else-if="block.type === 'links'" :key="index" class="hub-inline-links">
                  <h3>{{ block.title }}</h3>
                  <div>
                    <router-link v-for="item in block.items" :key="item.label" :to="localizedPath(item.path)">{{ item.label }}</router-link>
                  </div>
                </div>
                <div v-else-if="block.type === 'table'" :key="index" class="hub-table-block">
                  <div class="hub-theme-box" :class="{ 'hub-theme-overview': block.variant === 'overview' }">
                    <div class="hub-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th v-for="col in block.columns" :key="col">{{ col }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                            <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </template>
              </template>
            </section>
          </template>

          <section class="hub-main-feature" v-if="article && article.target">
            <div class="hub-main-feature-cta">
              <span>{{ layoutProfile.sideCtaKicker }}</span>
              <h2>{{ hubText('mainFeatureTitle') }}</h2>
              <p>{{ layoutProfile.sideCtaDesc }}</p>
              <router-link :to="localizedPath(article.target.path)" class="hub-main-feature-btn">
                {{ freeTrialLabel(article.target.label) }}
              </router-link>
            </div>
            <div class="hub-main-feature-links" v-if="featureLinks.length">
              <h3>{{ hubText('mainFeatureLinksTitle') }}</h3>
              <div class="hub-main-feature-grid">
                <router-link
                  v-for="item in featureLinks"
                  :key="'main-feat-' + item.path"
                  :to="localizedPath(item.path)"
                >{{ item.label }}</router-link>
              </div>
            </div>
          </section>

          <section class="hub-related-section" v-if="relatedArticles.length">
            <h2>{{ hubText('relatedTitle') }}</h2>
            <div class="hub-related-grid">
              <router-link v-for="item in relatedArticles" :key="item.slug" :to="articlePath(item.slug)" class="hub-related-card">
                <div class="hub-related-cover">
                  <img :src="articleCover(item)" :alt="item.title" loading="lazy">
                </div>
                <span>{{ categoryLabel(item.category) }}</span>
                <strong>{{ item.title }}</strong>
                <p>{{ item.excerpt }}</p>
              </router-link>
            </div>
          </section>
          </div>
        </div>

        <aside class="hub-article-sidebar">
          <div class="hub-sticky">
            <div class="hub-toc">
              <h2>{{ hubText('tocTitle') }}</h2>
              <div ref="sidebarTocScroll" class="hub-toc-scroll">
                <a
                  v-for="item in tocItems"
                  :key="'toc-' + item.id"
                  :href="'#' + item.id"
                  :data-toc-id="item.id"
                  :class="{ 'is-active': activeTocId === item.id }"
                >{{ item.title }}</a>
              </div>
            </div>

            <div class="hub-side-cta">
              <span>{{ layoutProfile.sideCtaKicker }}</span>
              <h2>{{ article.target.label }}</h2>
              <p>{{ layoutProfile.sideCtaDesc }}</p>
              <router-link :to="localizedPath(article.target.path)">{{ freeTrialLabel(article.target.label) }}</router-link>
            </div>

            <div class="hub-side-box hub-side-features">
              <h2>{{ hubText('featureEntry') }}</h2>
              <router-link
                v-for="item in featureLinks"
                :key="'feat-' + item.path"
                :to="localizedPath(item.path)"
              >{{ item.label }}</router-link>
            </div>

            <div v-if="sidebarFeaturedArticles.length" class="hub-side-box hub-side-reads">
              <h2>{{ hubText('featuredReading') }}</h2>
              <router-link
                v-for="item in sidebarFeaturedArticles"
                :key="'sf-' + item.slug"
                :to="articlePath(item.slug)"
              >
                <strong>{{ item.title }}</strong>
                <span>{{ categoryLabel(item.category) }}</span>
              </router-link>
            </div>
          </div>
        </aside>
      </article>
    </div>
    <div class="hub-detail-inner" v-else>
      <div class="hub-not-found">
        <h1>{{ hubText('notFoundTitle') }}</h1>
        <router-link :to="hubPath">{{ hubText('backToBlog') }}</router-link>
      </div>
    </div>
  </div>
</template>

<script>
var hubConfig = require('../config/hub')

module.exports = {
  name: 'HubDetail',
  data: function () {
    return {
      inArticleNavOpen: true,
      openNavSections: {},
      expandedCollapsibleSections: {},
      activeTocId: '',
      tocScrollTick: null
    }
  },
  watch: {
    article: {
      immediate: true,
      handler: function () {
        this.resetInArticleNavState()
        var self = this
        this.$nextTick(function () {
          self.updateActiveTocFromScroll()
        })
      }
    }
  },
  mounted: function () {
    window.addEventListener('scroll', this.onPageScroll, { passive: true })
    this.updateActiveTocFromScroll()
  },
  beforeDestroy: function () {
    window.removeEventListener('scroll', this.onPageScroll)
    if (this.tocScrollTick) {
      cancelAnimationFrame(this.tocScrollTick)
      this.tocScrollTick = null
    }
  },
  computed: {
    article: function () {
      return hubConfig.getHubArticle(this.currentHubLocale, this.$route.params.slug)
    },
    relatedArticles: function () {
      return hubConfig.getRelatedArticles(this.currentHubLocale, this.article)
    },
    featureLinks: function () {
      return hubConfig.getHubFeatureLinks(this.currentHubLocale)
    },
    sidebarFeaturedArticles: function () {
      var featured = hubConfig.getHubFeaturedArticles(this.currentHubLocale, 'all')
      var slug = this.article ? this.article.slug : ''
      return featured.filter(function (item) {
        return item.slug !== slug
      }).slice(0, 5)
    },
    isReviewLayout: function () {
      return this.article && this.article.layout === 'review'
    },
    isAlternativesLayout: function () {
      return this.article && this.article.layout === 'alternatives'
    },
    isHowToLayout: function () {
      return this.article && this.article.layout === 'how-to'
    },
    isPromptGuideLayout: function () {
      return this.article && this.article.layout === 'prompt-guide'
    },
    categoryLayoutClass: function () {
      if (!this.article) return ''
      return 'hub-cat-' + this.article.category
    },
    layoutProfile: function () {
      return hubConfig.layoutProfiles.getHubLayoutProfile(this.article, this.currentHubLocale)
    },
    showWorkflowStrip: function () {
      if (!this.layoutProfile.showWorkflowStrip || !this.article) return false
      var path = this.article.target && this.article.target.path ? this.article.target.path : ''
      return path.indexOf('/tools/') === 0
    },
    showEditorNote: function () {
      return this.isHowToLayout || this.isPromptGuideLayout
    },
    stepsBlockTitle: function () {
      if (!this.article) return this.hubText('defaultStepTitle')
      if (this.article.category === 'alternatives') return this.hubText('alternativesStepTitle')
      if (this.article.category === 'how-to-guides') return this.hubText('howToStepTitle')
      if (this.article.category === 'prompt-guides') return this.hubText('promptStepTitle')
      return this.hubText('reviewStepTitle')
    },
    creatingSteps: function () {
      return this.article ? (this.article.creatingSteps || []) : []
    },
    pros: function () {
      return this.article ? (this.article.pros || []) : []
    },
    cons: function () {
      return this.article ? (this.article.cons || []) : []
    },
    prosConsRows: function () {
      var pros = this.pros
      var cons = this.cons
      var len = Math.max(pros.length, cons.length)
      var rows = []
      var i
      for (i = 0; i < len; i++) {
        rows.push({ pro: pros[i] || '', con: cons[i] || '' })
      }
      return rows
    },
    editorNote: function () {
      if (!this.article) return ''
      if (this.article.target && this.article.target.path && this.article.target.path.indexOf('/tools/') === 0) {
        return this.hubText('editorNoteTool')
      }
      if (this.isHowToLayout || this.isPromptGuideLayout) {
        return this.hubText('editorNoteGuide')
      }
      return this.hubText('editorNoteDefault')
    },
    inArticleNavTree: function () {
      if (!this.article) return []
      return hubConfig.buildArticleNavTree(this.currentHubLocale, this.article)
    },
    inArticleNav: function () {
      return this.inArticleNavTree.map(function (item) {
        return { id: item.id, title: item.title }
      })
    },
    tocItems: function () {
      return this.inArticleNav
    },
    quickAnchorItems: function () {
      if (!this.article || !this.article.sections) return []
      var skip = {
        tldr: true,
        conclusion: true,
        'pros-cons': true
      }
      var out = []
      var sections = this.article.sections
      var i
      for (i = 0; i < sections.length; i++) {
        if (skip[sections[i].id]) continue
        out.push({
          id: sections[i].id,
          title: sections[i].title
        })
        if (out.length >= 7) break
      }
      return out
    },
    canonicalPath: function () {
      if (!this.article) return this.$route.path
      var prefix = this.currentHubLocale === 'en' ? '' : '/' + this.currentHubLocale
      return prefix + '/hub/articles/' + this.article.slug
    },
    langPrefix: function () {
      if (this.routeLangPrefix) return this.routeLangPrefix
      return this.currentHubLocale === 'en' ? '' : '/' + this.currentHubLocale
    },
    routeLangPrefix: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang : ''
    },
    currentHubLocale: function () {
      return hubConfig.normalizeHubLocale(this.$route.params.lang || hubConfig.DEFAULT_LOCALE)
    },
    homePath: function () {
      return this.routeLangPrefix ? this.routeLangPrefix : '/'
    },
    hubPath: function () {
      return this.langPrefix + '/hub'
    },
    coverImageUrl: function () {
      if (!this.article) return ''
      return hubConfig.demoImages.getArticleCoverImage(this.article)
    },
    heroImageUrl: function () {
      if (!this.article || !this.article.heroImage) return ''
      return this.resolveHubImage(this.article.heroImage)
    },
    comparePills: function () {
      return hubConfig.getHubTextList(this.currentHubLocale, 'comparePills')
    },
    workflowSteps: function () {
      return hubConfig.getHubTextList(this.currentHubLocale, 'workflowSteps')
    }
  },
  methods: {
    hubText: function (key) {
      return hubConfig.getHubText(this.currentHubLocale, key)
    },
    resetInArticleNavState: function () {
      var tree = this.inArticleNavTree
      var open = {}
      var i
      var opened = 0
      for (i = 0; i < tree.length; i++) {
        if (tree[i].children.length && opened < 2) {
          open[tree[i].id] = true
          opened++
        } else {
          open[tree[i].id] = false
        }
      }
      this.openNavSections = open
      this.expandedCollapsibleSections = {}
      this.inArticleNavOpen = true
    },
    toggleInArticleNav: function () {
      this.inArticleNavOpen = !this.inArticleNavOpen
    },
    toggleNavSection: function (sectionId) {
      this.$set(this.openNavSections, sectionId, !this.isNavSectionOpen(sectionId))
    },
    isNavSectionOpen: function (sectionId) {
      return !!this.openNavSections[sectionId]
    },
    isCollapsibleExpanded: function (sectionId) {
      return !!this.expandedCollapsibleSections[sectionId]
    },
    toggleCollapsibleSection: function (sectionId, listIdPrefix) {
      var wasExpanded = this.isCollapsibleExpanded(sectionId)
      this.$set(this.expandedCollapsibleSections, sectionId, !wasExpanded)
      if (wasExpanded) {
        var self = this
        this.$nextTick(function () {
          self.scrollCollapsiblePanelIntoView(sectionId, listIdPrefix)
        })
      }
    },
    scrollCollapsiblePanelIntoView: function (sectionId, listIdPrefix) {
      var el = document.getElementById(listIdPrefix + sectionId)
      if (!el) el = document.getElementById(sectionId)
      if (!el) return
      var offset = 88
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
      var top = el.getBoundingClientRect().top + scrollTop - offset
      if (top < 0) top = 0
      if (window.scrollTo) {
        try {
          window.scrollTo({ top: top, behavior: 'smooth' })
          return
        } catch (e) {
          // IE / older browsers
        }
      }
      window.scrollTo(0, top)
    },
    hubCollapsibleLimit: function () {
      return hubConfig.HUB_COLLAPSIBLE_VISIBLE_COUNT
    },
    countBlocksByType: function (section, blockType) {
      return hubConfig.countHubSectionBlocks(section, blockType)
    },
    shouldLimitSectionBlocks: function (section, sectionId, blockType) {
      return hubConfig.shouldCollapseHubSection(section, sectionId, blockType)
    },
    shouldLimitSearchIntents: function (section) {
      return this.shouldLimitSectionBlocks(section, 'search-intents', 'scenario-item')
    },
    shouldLimitFaq: function (section) {
      return this.shouldLimitSectionBlocks(section, 'faq', 'faq-item')
    },
    hiddenSectionBlockCount: function (section, blockType) {
      return hubConfig.hiddenHubSectionBlockCount(section, blockType)
    },
    searchIntentPrimaryEntries: function (section) {
      return hubConfig.buildHubCollapsibleEntries(section, 'scenario-item', 'primary')
    },
    searchIntentExtraEntries: function (section) {
      return hubConfig.buildHubCollapsibleEntries(section, 'scenario-item', 'extra')
    },
    faqPrimaryEntries: function (section) {
      return hubConfig.buildHubCollapsibleEntries(section, 'faq-item', 'primary')
    },
    faqExtraEntries: function (section) {
      return hubConfig.buildHubCollapsibleEntries(section, 'faq-item', 'extra')
    },
    buildCollapsibleEntries: function (section, blockType, mode) {
      return hubConfig.buildHubCollapsibleEntries(section, blockType, mode)
    },
    collapsibleToggleLabel: function (section, blockType) {
      if (this.isCollapsibleExpanded(section.id)) return this.hubText('collapse')
      return this.hubText('expandMore')
    },
    onPageScroll: function () {
      if (this.tocScrollTick) return
      var self = this
      this.tocScrollTick = requestAnimationFrame(function () {
        self.tocScrollTick = null
        self.updateActiveTocFromScroll()
      })
    },
    updateActiveTocFromScroll: function () {
      var sections = this.inArticleNav
      if (!sections.length) return
      var marker = 120
      var activeId = sections[0].id
      var i
      for (i = 0; i < sections.length; i++) {
        var el = document.getElementById(sections[i].id)
        if (!el) continue
        if (el.getBoundingClientRect().top <= marker) {
          activeId = sections[i].id
        }
      }
      if (activeId !== this.activeTocId) {
        this.activeTocId = activeId
        this.scrollActiveTocIntoView()
      }
    },
    scrollActiveTocIntoView: function () {
      var self = this
      this.$nextTick(function () {
        var scrollEl = self.$refs.sidebarTocScroll
        if (!scrollEl || !self.activeTocId) return
        var activeEl = scrollEl.querySelector('[data-toc-id="' + self.activeTocId + '"]')
        if (!activeEl) return
        var offsetTop = activeEl.offsetTop
        var scrollTop = scrollEl.scrollTop
        var clientHeight = scrollEl.clientHeight
        var itemHeight = activeEl.offsetHeight
        if (offsetTop < scrollTop + 8) {
          scrollEl.scrollTop = Math.max(0, offsetTop - 8)
        } else if (offsetTop + itemHeight > scrollTop + clientHeight - 8) {
          scrollEl.scrollTop = offsetTop + itemHeight - clientHeight + 8
        }
      })
    },
    categoryPath: function (key) {
      return this.langPrefix + '/hub/' + key
    },
    articlePath: function (slug) {
      return this.langPrefix + '/hub/articles/' + slug
    },
    hubSearchRoute: function (term) {
      return {
        path: this.hubPath,
        query: { q: term }
      }
    },
    localizedPath: function (path) {
      if (path.indexOf('/zh/') === 0 || path === '/zh') return path
      if (path.indexOf('/hub/') === 0) return this.langPrefix + path
      return this.langPrefix + path
    },
    freeTrialLabel: function (label) {
      return hubConfig.getFreeTrialLabel(this.currentHubLocale, label)
    },
    ctaButtonLabel: function (block) {
      if (!block || !block.buttonPath) return block && block.buttonLabel ? block.buttonLabel : ''
      if (block.buttonPath.indexOf('/image-models/') !== 0 && block.buttonPath.indexOf('/tools/') !== 0) {
        return block.buttonLabel
      }
      return this.freeTrialLabel(block.buttonLabel)
    },
    categoryLabel: function (key) {
      var cat = hubConfig.getHubCategory(this.currentHubLocale, key)
      return cat ? cat.label : key
    },
    escapeHtml: function (text) {
      return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    escapeRegExp: function (text) {
      return String(text || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    hubInlineLinkTargets: function () {
      return hubConfig.getInlineLinkTargets(this.currentHubLocale)
    },
    renderInlineText: function (text) {
      var escaped = this.escapeHtml(text)
      var targets = this.hubInlineLinkTargets()
      var self = this
      var labels = targets.map(function (item) {
        return self.escapeRegExp(self.escapeHtml(item.label))
      }).join('|')
      if (!labels) return escaped
      var map = {}
      targets.forEach(function (item) {
        map[self.escapeHtml(item.label)] = item.path
      })
      return escaped.replace(new RegExp(labels, 'g'), function (match) {
        var path = map[match]
        if (!path) return match
        return '<a class="hub-inline-link" href="' + self.localizedPath(path) + '">' + match + '</a>'
      })
    },
    resolveHubImage: function (imageKey) {
      return hubConfig.demoImages.getHubDemoImage(imageKey)
    },
    articleCover: function (item) {
      return hubConfig.demoImages.getArticleCoverImage(item)
    },
    copyPrompt: function (text, event) {
      if (!text) return
      var btn = event && event.target
      var self = this
      function markCopied() {
        if (!btn) return
        var original = btn.getAttribute('data-label') || btn.textContent
        btn.setAttribute('data-label', original)
        btn.textContent = self.hubText('copied')
        setTimeout(function () {
          btn.textContent = original
        }, 1600)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(markCopied)
        return
      }
      var input = document.createElement('textarea')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      markCopied()
    },
    collectFaqItems: function () {
      if (!this.article) return []
      var faqs = []
      var sections = this.article.sections || []
      for (var si = 0; si < sections.length; si++) {
        var blocks = sections[si].blocks || []
        for (var bi = 0; bi < blocks.length; bi++) {
          if (blocks[bi].type === 'faq-item') {
            faqs.push(blocks[bi])
          }
        }
      }
      return faqs
    },
    buildJsonLd: function () {
      if (!this.article) return []
      var url = 'https://www.miralvis.com' + this.canonicalPath
      var breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: this.hubText('home'), item: 'https://www.miralvis.com' + this.langPrefix },
          { '@type': 'ListItem', position: 2, name: this.hubText('blog'), item: 'https://www.miralvis.com' + this.langPrefix + '/hub' },
          { '@type': 'ListItem', position: 3, name: this.categoryLabel(this.article.category), item: 'https://www.miralvis.com' + this.langPrefix + '/hub/' + this.article.category },
          { '@type': 'ListItem', position: 4, name: this.article.title, item: url }
        ]
      }
      var article = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: this.article.title,
        description: this.article.metaDescription,
        dateModified: this.article.updatedAt,
        datePublished: this.article.updatedAt,
        author: { '@type': 'Organization', name: 'Miral AI' },
        publisher: { '@type': 'Organization', name: 'Miral AI' },
        mainEntityOfPage: url
      }
      if (this.coverImageUrl) {
        article.image = this.coverImageUrl
      }
      var scripts = [breadcrumb, article]
      var faqs = this.collectFaqItems()
      if (faqs.length) {
        scripts.push({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(function (item) {
            return {
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer
              }
            }
          })
        })
      }
      return scripts
    }
  },
  metaInfo: function () {
    if (!this.article) {
      return { title: this.hubText('notFoundMetaTitle') }
    }
    var canonical = 'https://www.miralvis.com' + this.canonicalPath
    var scripts = this.buildJsonLd().map(function (item) {
      return {
        type: 'application/ld+json',
        json: item
      }
    })
    var meta = [
      { vmid: 'description', name: 'description', content: this.article.metaDescription },
      { vmid: 'og:type', property: 'og:type', content: 'article' },
      { vmid: 'og:title', property: 'og:title', content: this.article.metaTitle },
      { vmid: 'og:description', property: 'og:description', content: this.article.metaDescription },
      { vmid: 'og:url', property: 'og:url', content: canonical },
      { vmid: 'twitter:title', name: 'twitter:title', content: this.article.metaTitle },
      { vmid: 'twitter:description', name: 'twitter:description', content: this.article.metaDescription }
    ]
    if (this.coverImageUrl) {
      meta.push({ vmid: 'og:image', property: 'og:image', content: this.coverImageUrl })
    }
    var articlePath = '/hub/articles/' + this.article.slug
    var articleLocales = this.article.supportedLocales || hubConfig.SUPPORTED_LOCALES
    var alternateLinks = articleLocales.map(function (locale) {
      var prefix = locale === hubConfig.DEFAULT_LOCALE ? '' : '/' + locale
      return {
        vmid: 'hub-article-alternate-' + locale,
        rel: 'alternate',
        hreflang: locale,
        href: 'https://www.miralvis.com' + prefix + articlePath
      }
    })
    alternateLinks.unshift({ rel: 'canonical', href: canonical })
    alternateLinks.push({
      vmid: 'hub-article-alternate-x-default',
      rel: 'alternate',
      hreflang: 'x-default',
      href: 'https://www.miralvis.com' + articlePath
    })
    return {
      title: this.article.metaTitle,
      meta: meta,
      link: alternateLinks,
      script: scripts
    }
  }
}
</script>

<style scoped>
.hub-detail-page {
  min-height: 100vh;
  background: #f7f7f8;
  color: #1a1a2e;
}
.hub-layout-review,
.hub-layout-alternatives,
.hub-layout-prompt-guide,
.hub-layout-how-to {
  --hub-accent: #059669;
  --hub-accent-soft: rgba(5, 150, 105, 0.08);
  --hub-accent-border: rgba(5, 150, 105, 0.22);
  --hub-header-bg: linear-gradient(180deg, rgba(5, 150, 105, 0.06) 0%, #fff 100%);
}
.hub-header-hint {
  margin: 0 0 16px;
  color: #666;
  font-size: 15px;
  line-height: 1.7;
}
.hub-header-hero {
  margin: 0 0 18px;
  line-height: 0;
}
.hub-header-hero img {
  display: block;
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}
.hub-compare-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 16px;
}
.hub-compare-pills span {
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--hub-accent-soft);
  border: 1px solid var(--hub-accent-border);
  color: var(--hub-accent);
  font-size: 13px;
  font-weight: 700;
}
.hub-workflow-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px dashed var(--hub-accent-border);
  background: var(--hub-accent-soft);
  color: #374151;
  font-size: 13px;
  font-weight: 700;
}
.hub-workflow-arrow {
  color: var(--hub-accent);
  font-weight: 800;
}
.hub-tldr-module {
  padding: 22px 22px 8px;
  border-radius: 12px;
  border: 1px solid var(--hub-accent-border);
  background: var(--hub-accent-soft);
}
.hub-tldr-module .hub-module-head > span {
  color: var(--hub-accent);
}
.hub-tldr-module .hub-module-head h2 {
  margin-bottom: 6px;
}
.hub-tldr-module .hub-module-head p {
  margin: 0 0 14px;
  color: #666;
  font-size: 14px;
  line-height: 1.65;
}
.hub-tldr-module .hub-theme-box {
  border-color: #e5e7eb;
  background: #fff;
}
.hub-section-tldr {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}
.hub-detail-page .hub-meta-category {
  color: var(--hub-accent);
  font-weight: 600;
}
.hub-detail-page .hub-meta-category:hover {
  text-decoration: underline;
}
.hub-detail-page .hub-primary-cta {
  background: var(--hub-accent);
}
.hub-detail-page .hub-secondary-cta {
  color: var(--hub-accent);
}
.hub-detail-page .hub-module-head > span {
  color: var(--hub-accent);
}
.hub-detail-page .hub-in-article-list::before {
  background: linear-gradient(180deg, var(--hub-accent-border) 0%, #e5e7eb 100%);
}
.hub-detail-page .hub-in-article-dot {
  border-color: var(--hub-accent);
}
.hub-detail-page .hub-in-article-row:hover .hub-in-article-dot {
  background: var(--hub-accent);
}
.hub-detail-page .hub-in-article-text {
  text-decoration-color: var(--hub-accent-border);
}
.hub-detail-page .hub-in-article-text:hover {
  color: var(--hub-accent);
  text-decoration-color: var(--hub-accent);
}
.hub-detail-page .hub-faq-entry h3::before {
  background: var(--hub-accent-soft);
  color: var(--hub-accent);
}
.hub-detail-page .hub-side-cta {
  background: var(--hub-accent-soft);
  border-color: var(--hub-accent-border);
  border-left-color: var(--hub-accent);
}
.hub-detail-page .hub-side-cta span {
  color: var(--hub-accent);
}
.hub-detail-page .hub-side-cta a {
  background: var(--hub-accent);
}
.hub-detail-page .hub-copy-btn {
  color: var(--hub-accent);
  border-color: var(--hub-accent-border);
}
.hub-detail-page .hub-copy-btn:hover {
  border-color: var(--hub-accent);
}
.hub-detail-page .hub-prompt-label {
  color: var(--hub-accent);
}
.hub-detail-page .hub-toc-scroll a.is-active {
  color: var(--hub-accent);
  background: var(--hub-accent-soft);
}
.hub-detail-page .hub-related-card span {
  color: var(--hub-accent);
}
.hub-detail-page .hub-search-topic:hover {
  color: var(--hub-accent);
  border-color: var(--hub-accent-border);
  background: var(--hub-accent-soft);
}
.hub-detail-page .hub-inline-cta a {
  background: var(--hub-accent);
}
.hub-layout-review .hub-creating-step > span {
  background: var(--hub-accent);
}
.hub-layout-review .hub-pros-cons-theme {
  border-color: var(--hub-accent-border);
}
.hub-layout-alternatives .hub-section-platform-ref .hub-theme-overview th {
  background: var(--hub-accent-soft);
  color: var(--hub-accent);
}
.hub-layout-prompt-guide .hub-editor-note {
  border-left-color: var(--hub-accent);
  background: var(--hub-accent-soft);
}
.hub-layout-prompt-guide .hub-section-prompts {
  padding: 4px 0 8px;
}
.hub-layout-prompt-guide .hub-prompt-stack-item,
.hub-layout-prompt-guide .hub-prompt-stack-item-inline {
  padding: 18px;
  border: 1px solid var(--hub-accent-border);
  border-radius: 12px;
  background: #fff;
  margin-bottom: 16px;
}
.hub-layout-prompt-guide .hub-prompt-stack-text {
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--hub-accent-soft);
  border: 1px solid var(--hub-accent-border);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
}
.hub-layout-prompt-guide .hub-copy-btn {
  background: #fff;
}
.hub-layout-how-to .hub-creating-steps-inline {
  position: relative;
  padding-left: 4px;
}
.hub-layout-how-to .hub-creating-steps-inline::before {
  content: '';
  position: absolute;
  left: 21px;
  top: 18px;
  bottom: 18px;
  width: 2px;
  background: linear-gradient(180deg, var(--hub-accent) 0%, var(--hub-accent-border) 100%);
  border-radius: 1px;
}
.hub-layout-how-to .hub-creating-step {
  position: relative;
  border-color: var(--hub-accent-border);
  background: #fff;
}
.hub-layout-how-to .hub-creating-step > span {
  background: var(--hub-accent);
}
.hub-layout-how-to .hub-media-figure img,
.hub-layout-how-to .hub-media-item img {
  border: 1px solid var(--hub-accent-border);
}
.hub-layout-how-to .hub-editor-note {
  border-left-color: var(--hub-accent);
  background: var(--hub-accent-soft);
}
.hub-layout-how-to .hub-section-platform-ref {
  border-left: 3px solid var(--hub-accent);
  padding-left: 18px;
}
.hub-layout-review .hub-header-hero img {
  box-shadow: 0 8px 24px rgba(5, 150, 105, 0.12);
}
.hub-layout-alternatives .hub-tldr-module {
  border-style: dashed;
}
.hub-layout-alternatives .hub-compare-pills span {
  border-radius: 6px;
}
.hub-layout-prompt-guide .hub-tldr-module {
  background: #fff;
}
.hub-layout-prompt-guide .hub-section-prompts h2 {
  padding-bottom: 12px;
  border-bottom: 1px solid var(--hub-accent-border);
}
.hub-layout-prompt-guide .hub-prompt-stack {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
}
.hub-layout-how-to .hub-tldr-module {
  border-style: solid;
  background: #fff;
}
.hub-detail-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 34px 24px 82px;
}
.hub-detail-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 18px;
  color: #8b8fa3;
  font-size: 13px;
}
.hub-detail-breadcrumb a:hover {
  color: #059669;
}
.hub-article-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 24px;
  align-items: start;
}
.hub-article-main {
  min-width: 0;
}
.hub-article-header,
.hub-related-section,
.hub-section-nav {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.hub-article-header {
  padding: 30px;
  background: var(--hub-header-bg, #fff);
}
.hub-article-body {
  margin-top: 16px;
  padding: 28px 30px 32px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.hub-article-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  margin-bottom: 14px;
  color: #8b8fa3;
  font-size: 13px;
  line-height: 1.5;
}
.hub-meta-hot {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 10px;
  min-height: 24px;
  padding: 3px 9px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.02em;
}
.hub-meta-hot-icon {
  font-size: 13px;
  line-height: 1;
}
.hub-meta-source {
  color: #6b7280;
  font-weight: 700;
}
.hub-meta-category {
  color: inherit;
  font-weight: 600;
  text-decoration: none;
}
.hub-meta-sep {
  margin: 0 8px;
  color: #c4c8d4;
  user-select: none;
}
.hub-meta-item {
  color: #8b8fa3;
}
.hub-article-header h1 {
  margin: 0 0 16px;
  font-size: 40px;
  line-height: 1.18;
}
.hub-lead {
  margin: 0 0 16px;
  color: #444;
  font-size: 18px;
  line-height: 1.75;
}
.hub-editor-note {
  margin: 0 0 18px;
  padding: 14px 16px;
  border-left: 3px solid #059669;
  border-radius: 8px;
  background: rgba(5, 150, 105, 0.06);
  color: #374151;
  font-size: 15px;
  line-height: 1.75;
}
.hub-hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.hub-primary-cta,
.hub-secondary-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 9px 15px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
}
.hub-primary-cta {
  background: #059669;
  color: #fff;
}
.hub-secondary-cta {
  border: 1px solid #e5e7eb;
  color: #059669;
}
/* Pollo 风格 theme 容器 */
.hub-theme-box {
  width: 100%;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}
.hub-theme-box ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.hub-hero-shot {
  display: none;
}
.hub-media-figure {
  margin: 20px 0;
}
.hub-media-figure img,
.hub-media-item img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: none;
  background: transparent;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}
.hub-media-gallery {
  margin: 20px 0;
}
.hub-media-grid {
  display: grid;
  gap: 16px;
}
.hub-media-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hub-media-cols-4 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (min-width: 900px) {
  .hub-media-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.hub-media-item {
  margin: 0;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: 8px;
  background: #f3f4f6;
}
.hub-media-gallery-landscape .hub-media-item {
  aspect-ratio: 3 / 2;
}
.hub-media-gallery-portrait .hub-media-item {
  aspect-ratio: 3 / 4;
}
.hub-media-gallery .hub-media-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0;
}
.hub-media-gallery-contain .hub-media-item {
  padding: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
}
.hub-media-gallery-contain .hub-media-item img {
  object-fit: contain;
}
.hub-media-figure figcaption,
.hub-media-caption {
  margin: 10px 0 0;
  color: #666;
  font-size: 14px;
  line-height: 1.65;
}
.hub-theme-overview .hub-table-wrap {
  margin: 0;
}
.hub-theme-overview table {
  border: none;
  min-width: 0;
}
.hub-theme-overview th,
.hub-theme-overview td {
  border-bottom: 1px solid #eef0f3;
}
.hub-theme-overview tr:last-child td {
  border-bottom: none;
}
.hub-tip-info {
  margin: 16px 0 0;
  padding: 16px 18px;
  border-color: rgba(5, 150, 105, 0.28);
  background: linear-gradient(180deg, rgba(5, 150, 105, 0.07) 0%, rgba(5, 150, 105, 0.03) 100%);
}
.hub-tip-info strong {
  display: block;
  margin-bottom: 8px;
  color: #047857;
  font-size: 15px;
}
.hub-tip-info p {
  margin: 0;
  color: #374151;
  font-size: 15px;
  line-height: 1.75;
}
.hub-pros-cons-theme {
  padding: 0;
}
.hub-pros-cons-table {
  width: 100%;
  border-collapse: collapse;
}
.hub-pros-cons-table th,
.hub-pros-cons-table td {
  width: 50%;
  padding: 14px 16px;
  border-bottom: 1px solid #eef0f3;
  text-align: left;
  vertical-align: top;
  font-size: 14px;
  line-height: 1.65;
  color: #444;
}
.hub-pros-cons-table th {
  background: #f9fafb;
  color: #1a1a2e;
  font-weight: 800;
  font-size: 13px;
}
.hub-pros-cons-table tr:last-child td {
  border-bottom: none;
}
.hub-pros-cons-table td:first-child {
  border-right: 1px solid #eef0f3;
}
.hub-table-block {
  margin: 0 0 4px;
}
.hub-prompt-examples-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  align-items: stretch;
}
.hub-prompt-card {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}
.hub-prompt-card-single {
  margin-bottom: 14px;
  max-width: 520px;
}
.hub-prompt-card-media {
  line-height: 0;
  background: #f3f4f6;
  border-bottom: 1px solid #eef0f3;
  padding: 10px;
}
.hub-prompt-card-media img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 240px;
  object-fit: contain;
  border-radius: 6px;
}
.hub-prompt-card-body {
  padding: 16px 18px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  flex: 1;
}
.hub-prompt-examples-grid .hub-prompt-label {
  min-height: 20px;
}
.hub-prompt-text {
  margin: 0 0 12px;
  color: #333;
  font-size: 15px;
  line-height: 1.7;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  min-height: 3.4em;
  max-height: 3.4em;
}
.hub-prompt-examples-grid .hub-prompt-text {
  margin: 0 0 12px;
}
.hub-prompt-examples-grid .hub-copy-btn {
  margin-top: auto;
  align-self: flex-start;
}
.hub-prompt-module .hub-module-head {
  margin-bottom: 16px;
}
.hub-prompt-module .hub-module-head h2 {
  margin: 0;
  font-size: 26px;
  line-height: 1.3;
}
.hub-inline-cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin: 16px 0;
  padding: 20px 22px;
  border-color: #0f172a;
  background: #0f172a;
  color: #fff;
}
.hub-in-article-nav {
  margin: 0 0 28px;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f3f4f6;
  overflow: hidden;
}
.hub-in-article-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  margin: 0;
  border: none;
  background: #f3f4f6;
  cursor: pointer;
  text-align: left;
}
.hub-in-article-nav.is-expanded .hub-in-article-toggle {
  border-bottom: 1px solid #e5e7eb;
}
.hub-in-article-toggle-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.hub-in-article-menu-icon {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.hub-in-article-menu-icon span {
  display: block;
  width: 14px;
  height: 2px;
  background: #555;
  border-radius: 1px;
}
.hub-in-article-label {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #1a1a2e;
}
.hub-in-article-chevron {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-top: -2px;
  border-right: 2px solid #666;
  border-bottom: 2px solid #666;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.hub-in-article-chevron-sm {
  width: 7px;
  height: 7px;
  margin-top: 0;
}
.hub-in-article-chevron.is-open {
  transform: rotate(225deg);
  margin-top: 2px;
}
.hub-in-article-chevron-sm.is-open {
  margin-top: 1px;
}
.hub-in-article-scroll {
  max-height: 280px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px 16px 14px;
}
.hub-in-article-scroll::-webkit-scrollbar {
  width: 5px;
}
.hub-in-article-scroll::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 999px;
}
.hub-in-article-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.hub-in-article-list {
  position: relative;
  margin: 0;
  padding: 2px 0 2px 22px;
  list-style: none;
}
.hub-in-article-list::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 10px;
  bottom: 10px;
  width: 2px;
  background: linear-gradient(180deg, rgba(5, 150, 105, 0.35) 0%, #e5e7eb 100%);
  border-radius: 1px;
}
.hub-in-article-item {
  margin: 0;
}
.hub-in-article-row {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 8px 0;
}
.hub-in-article-dot {
  position: absolute;
  left: -22px;
  top: 12px;
  width: 10px;
  height: 10px;
  border: 2px solid #059669;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 0 2px #f3f4f6;
  flex-shrink: 0;
}
.hub-in-article-text {
  flex: 1;
  min-width: 0;
  padding-right: 8px;
  color: #444;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  text-decoration: underline;
  text-decoration-color: rgba(5, 150, 105, 0.35);
  text-underline-offset: 3px;
}
.hub-in-article-text:hover {
  color: #059669;
  text-decoration-color: #059669;
}
.hub-in-article-row:hover .hub-in-article-dot {
  background: #059669;
}
.hub-in-article-sub-toggle {
  flex-shrink: 0;
  margin-top: 2px;
  padding: 2px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
}
.hub-in-article-sub {
  margin: 0 0 6px 22px;
  padding: 0 0 4px 14px;
  list-style: none;
  border-left: 2px solid rgba(5, 150, 105, 0.12);
}
.hub-in-article-sub li {
  margin: 0;
}
.hub-in-article-sub a {
  display: block;
  padding: 6px 0;
  color: #666;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
  text-decoration: underline;
  text-decoration-color: rgba(5, 150, 105, 0.25);
  text-underline-offset: 2px;
}
.hub-in-article-sub a:hover {
  color: #059669;
  text-decoration-color: #059669;
}
.hub-quick-anchors {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 16px 0 22px;
  padding: 12px;
  border: 1px solid rgba(5, 150, 105, 0.16);
  border-radius: 8px;
  background: rgba(5, 150, 105, 0.04);
}
.hub-quick-anchors span {
  color: #047857;
  font-size: 13px;
  font-weight: 800;
}
.hub-quick-anchors a {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}
.hub-quick-anchors a:hover {
  color: #059669;
  background: rgba(5, 150, 105, 0.08);
}
.hub-article-body >>> .hub-inline-link {
  color: #059669;
  font-weight: inherit;
  text-decoration: none;
  text-underline-offset: 3px;
}
.hub-article-body >>> .hub-inline-link:hover {
  color: #047857;
  text-decoration: underline;
  text-decoration-color: #047857;
}
.hub-faq-entry {
  scroll-margin-top: 18px;
}
.hub-prompt-stack-item {
  scroll-margin-top: 18px;
}
.hub-steps-wrap {
  margin-top: 18px;
  scroll-margin-top: 18px;
}
.hub-steps-title {
  margin: 0 0 12px;
  font-size: 17px;
  line-height: 1.4;
}
.hub-creating-steps-inline {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hub-section-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding: 14px;
}
.hub-section-nav a {
  padding: 7px 10px;
  border-radius: 6px;
  background: #f7f7f8;
  color: #555;
  font-size: 13px;
  font-weight: 600;
}
.hub-article-module {
  margin-top: 16px;
  padding: 22px;
  scroll-margin-top: 18px;
}
.hub-module-head {
  margin-bottom: 16px;
}
.hub-module-head > span {
  display: inline-flex;
  margin-bottom: 8px;
  color: #059669;
  font-size: 12px;
  font-weight: 800;
}
.hub-module-head h2 {
  margin: 0 0 8px;
  font-size: 24px;
  line-height: 1.35;
}
.hub-module-head p {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.7;
}
.hub-navigation-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
.hub-navigation-grid a {
  min-height: 94px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f7f7f8;
}
.hub-navigation-grid strong,
.hub-navigation-grid span {
  display: block;
}
.hub-navigation-grid strong {
  margin-bottom: 7px;
  font-size: 14px;
}
.hub-navigation-grid span {
  color: #666;
  font-size: 12px;
}
.hub-creating-steps,
.hub-creating-steps-review {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
.hub-creating-step {
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}
.hub-creating-step > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-bottom: 10px;
  border-radius: 7px;
  background: #059669;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
}
.hub-creating-step strong {
  display: block;
  margin-bottom: 7px;
  font-size: 14px;
}
.hub-creating-step p {
  margin: 0;
  color: #666;
  font-size: 12px;
  line-height: 1.6;
}
.hub-pros-cons-grid {
  display: none;
}
.hub-article-section {
  margin-top: 36px;
  padding: 36px 0 0;
  border: none;
  background: transparent;
  border-top: 1px solid #eef0f3;
  border-radius: 0;
  scroll-margin-top: 18px;
}
.hub-article-body > .hub-in-article-nav + .hub-article-section,
.hub-article-body > .hub-article-section:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}
.hub-prompt-stack > h2 {
  margin: 0 0 20px;
  font-size: 26px;
  line-height: 1.3;
}
.hub-prompt-stack-item,
.hub-prompt-stack-item-inline {
  margin: 0 0 32px;
  padding: 0 0 32px;
  border-bottom: 1px solid #f0f1f3;
}
.hub-prompt-stack-item:last-child,
.hub-prompt-stack-item-inline:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}
.hub-prompt-stack-label {
  margin: 0 0 14px;
  font-size: 18px;
  line-height: 1.4;
  color: #1a1a2e;
}
.hub-prompt-stack-media {
  margin: 0 0 14px;
}
.hub-prompt-stack-text {
  margin: 0 0 12px;
  color: #333;
  font-size: 15px;
  line-height: 1.75;
}
.hub-section-tldr {
  background: transparent;
}
.hub-main-feature {
  margin-top: 36px;
  padding: 28px 30px;
  border: 1px solid rgba(5, 150, 105, 0.22);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(5, 150, 105, 0.08) 0%, #fff 100%);
}
.hub-main-feature-cta span {
  display: block;
  margin-bottom: 8px;
  color: #059669;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.02em;
}
.hub-main-feature-cta h2 {
  margin: 0 0 10px;
  font-size: 26px;
  line-height: 1.3;
  color: #1a1a2e;
}
.hub-main-feature-cta p {
  max-width: 640px;
  margin: 0 0 18px;
  color: #555;
  font-size: 15px;
  line-height: 1.7;
}
.hub-main-feature-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 22px;
  border-radius: 8px;
  background: #059669;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.15s ease;
}
.hub-main-feature-btn:hover {
  background: #047857;
  color: #fff;
}
.hub-main-feature-links {
  margin-top: 24px;
  padding-top: 22px;
  border-top: 1px solid rgba(5, 150, 105, 0.14);
}
.hub-main-feature-links h3 {
  margin: 0 0 14px;
  font-size: 15px;
  color: #1a1a2e;
}
.hub-main-feature-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.hub-main-feature-grid a {
  display: flex;
  align-items: center;
  min-height: 42px;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #444;
  font-size: 14px;
  line-height: 1.4;
  text-decoration: none;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.hub-main-feature-grid a:hover {
  border-color: rgba(5, 150, 105, 0.35);
  color: #059669;
  background: rgba(5, 150, 105, 0.05);
}
.hub-related-section {
  margin-top: 36px;
  padding: 36px 0 0;
  border: none;
  background: transparent;
  border-top: 1px solid #eef0f3;
  border-radius: 0;
}
.hub-article-section h2,
.hub-related-section h2 {
  margin: 0 0 16px;
  font-size: 26px;
  line-height: 1.3;
}
.hub-article-section h3 {
  margin: 0 0 10px;
  font-size: 17px;
}
.hub-article-section p {
  margin: 0 0 14px;
  color: #444;
  font-size: 16px;
  line-height: 1.85;
}
.hub-bullet-list {
  display: grid;
  gap: 10px;
  margin: 0 0 16px;
  padding-left: 18px;
  list-style: disc;
}
.hub-prompt-label {
  display: inline-flex;
  margin-bottom: 8px;
  color: #059669;
  font-size: 12px;
  font-weight: 800;
}
.hub-copy-btn {
  padding: 8px 12px;
  border: 1px solid #d9eee6;
  border-radius: 7px;
  background: #fff;
  color: #047857;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.hub-copy-btn:hover {
  border-color: #059669;
}
.hub-prompt-text-list {
  display: grid;
  gap: 12px;
  margin-bottom: 14px;
}
.hub-prompt-text-card {
  padding: 16px 18px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}
.hub-prompt-text-card .hub-prompt-text {
  margin-bottom: 12px;
}
.hub-prompt-text-card .hub-copy-btn {
  align-self: flex-start;
}
.hub-faq-module {
  margin-top: 0;
}
.hub-section-intents {
  scroll-margin-top: 88px;
}
.hub-intents-module {
  display: block;
  margin-top: 0;
}
.hub-intents-module .hub-module-head,
.hub-intents-module .hub-intents-panel {
  width: 100%;
}
.hub-intents-panel {
  min-width: 0;
}
.hub-intents-desc {
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.65;
  color: #4b5563;
}
.hub-intents-module .hub-intent-card {
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}
.hub-intents-module .hub-intent-card + .hub-intent-card {
  margin-top: 10px;
}
.hub-intents-list,
.hub-faq-list {
  position: relative;
}
.hub-intents-panel.is-hub-collapsed .hub-intents-list::after,
.hub-faq-panel.is-hub-collapsed .hub-faq-list::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 140px;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.82) 38%,
    rgba(255, 255, 255, 0.97) 58%,
    #ffffff 72%,
    #ffffff 100%
  );
}
.hub-intents-extra .hub-intent-card:first-child {
  margin-top: 10px;
}
.hub-faq-panel {
  min-width: 0;
}
.hub-faq-extra .hub-faq-entry:first-child {
  border-top: 1px solid #eef0f3;
}
.hub-faq-extra .hub-faq-entry:last-child {
  border-bottom: none;
}
.hub-faq-list > .hub-faq-entry:has(+ .hub-faq-extra) {
  border-bottom: none;
}
.hub-intent-title {
  display: inline-block;
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #059669;
  text-decoration: none;
}
.hub-intent-title:hover {
  text-decoration: underline;
}
.hub-intents-module .hub-intent-card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
}
.hub-collapsible-more-wrap {
  text-align: center;
}
.hub-intents-panel.is-hub-collapsed .hub-intents-list,
.hub-faq-panel.is-hub-collapsed .hub-faq-list {
  padding-bottom: 54px;
}
.hub-intents-panel.is-hub-collapsed .hub-intents-more-wrap,
.hub-faq-panel.is-hub-collapsed .hub-faq-more-wrap {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  margin-top: 0;
  background: transparent;
}
.hub-intents-panel.is-hub-collapsed .hub-intents-more-wrap {
  padding: 0 0 4px;
}
.hub-faq-panel.is-hub-collapsed .hub-faq-more-wrap {
  padding: 0 22px 18px;
}
.hub-intents-panel:not(.is-hub-collapsed) .hub-intents-more-wrap,
.hub-faq-panel:not(.is-hub-collapsed) .hub-faq-more-wrap {
  position: static;
  margin-top: 0;
  border-top: 1px solid #eef0f3;
}
.hub-intents-panel:not(.is-hub-collapsed) .hub-intents-more-wrap {
  padding: 14px 0 4px;
}
.hub-faq-panel:not(.is-hub-collapsed) .hub-faq-more-wrap {
  padding: 14px 22px 18px;
}
.hub-collapsible-more {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 6px 18px 10px;
  border: none;
  background: transparent;
  color: var(--hub-accent, #059669);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  cursor: pointer;
}
.hub-collapsible-more:hover {
  color: #047857;
}
.hub-collapsible-more:hover .hub-collapsible-more-text {
  text-decoration: underline;
}
.hub-collapsible-more-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 20px;
  animation: hub-collapsible-chevron-bounce 1.5s ease-in-out infinite;
}
.hub-collapsible-more.is-expanded .hub-collapsible-more-icon {
  animation: none;
}
.hub-collapsible-more-icon::before,
.hub-collapsible-more-icon::after {
  content: '';
  display: block;
  width: 10px;
  height: 10px;
  border-right: 2.4px solid currentColor;
  border-bottom: 2.4px solid currentColor;
  transform: rotate(45deg);
  margin-top: -3px;
}
.hub-collapsible-more-icon::after {
  margin-top: -5px;
}
.hub-collapsible-more.is-expanded .hub-collapsible-more-icon::before,
.hub-collapsible-more.is-expanded .hub-collapsible-more-icon::after {
  transform: rotate(-135deg);
  margin-top: -3px;
}
.hub-collapsible-more.is-expanded .hub-collapsible-more-icon::after {
  margin-top: -5px;
}
@keyframes hub-collapsible-chevron-bounce {
  0%, 100% {
    transform: translateY(0);
    opacity: 0.75;
  }
  50% {
    transform: translateY(3px);
    opacity: 1;
  }
}
.hub-intents-module .hub-module-head {
  margin-bottom: 14px;
}
.hub-conclusion-module {
  margin-top: 0;
  padding-bottom: 8px;
}
.hub-conclusion-module .hub-module-head {
  margin-bottom: 12px;
}
.hub-conclusion-module > p {
  margin: 0 0 14px;
  color: #444;
  font-size: 16px;
  line-height: 1.75;
}
.hub-conclusion-list {
  margin: 0 0 18px;
}
.hub-conclusion-cta {
  margin-top: 4px;
}
.hub-section-platform-ref {
  padding-bottom: 8px;
}
.hub-section-platform-ref > h2 {
  margin-bottom: 10px;
}
.hub-faq-module .hub-module-head {
  margin-bottom: 14px;
}
.hub-faq-theme {
  padding: 0;
}
.hub-faq-entry {
  padding: 18px 22px;
  border-bottom: 1px solid #eef0f3;
}
.hub-faq-entry:last-child {
  border-bottom: none;
}
.hub-faq-entry h3 {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 8px;
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
}
.hub-faq-entry h3::before {
  content: 'Q';
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  border-radius: 6px;
  background: rgba(5, 150, 105, 0.1);
  color: #059669;
  font-size: 12px;
  font-weight: 800;
}
.hub-faq-entry p {
  margin: 0;
  padding-left: 32px;
  color: #555;
  font-size: 15px;
  line-height: 1.75;
}
.hub-section-faq {
  scroll-margin-top: 88px;
  padding-bottom: 22px;
}
.hub-inline-cta strong {
  display: block;
  margin-bottom: 6px;
  font-size: 18px;
}
.hub-inline-cta p {
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.6;
}
.hub-inline-cta a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 9px 16px;
  border-radius: 8px;
  background: #059669;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
}
.hub-conclusion-module .hub-conclusion-cta.hub-inline-cta {
  border-color: var(--hub-accent-border);
  background: linear-gradient(135deg, var(--hub-accent-soft) 0%, #fff 72%);
  color: #1a1a2e;
}
.hub-conclusion-module .hub-conclusion-cta.hub-inline-cta strong {
  color: #1a1a2e;
}
.hub-conclusion-module .hub-conclusion-cta.hub-inline-cta p {
  color: #555;
}
.hub-conclusion-module .hub-conclusion-cta.hub-inline-cta a {
  background: var(--hub-accent);
  color: #fff;
}
.hub-inline-links {
  margin-top: 14px;
  padding: 16px;
  border-radius: 8px;
  background: rgba(5, 150, 105, 0.05);
  border: 1px solid rgba(5, 150, 105, 0.18);
}
.hub-inline-links div {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
}
.hub-inline-links a {
  padding: 8px 11px;
  border-radius: 7px;
  background: #fff;
  border: 1px solid #d9eee6;
  color: #047857;
  font-size: 13px;
  font-weight: 700;
}
.hub-table-wrap {
  overflow-x: auto;
  margin: 14px 0 18px;
}
.hub-table-wrap table {
  width: 100%;
  border-collapse: collapse;
  min-width: 620px;
  border: 1px solid #e5e7eb;
}
.hub-table-wrap th,
.hub-table-wrap td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
  vertical-align: top;
  font-size: 14px;
  line-height: 1.6;
}
.hub-table-wrap th {
  background: #f7f7f8;
  font-weight: 800;
}
.hub-related-section {
  margin-top: 16px;
  padding: 26px 30px;
}
.hub-related-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.hub-related-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  padding-bottom: 14px;
}
.hub-related-cover {
  height: 148px;
  overflow: hidden;
  background: #eee;
}
.hub-related-cover img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.25s ease;
}
.hub-related-card:hover .hub-related-cover img {
  transform: scale(1.03);
}
.hub-related-card span {
  margin-top: 12px;
  color: #059669;
  font-size: 12px;
  font-weight: 800;
}
.hub-related-card span,
.hub-related-card strong,
.hub-related-card p {
  display: block;
  padding: 0 14px;
}
.hub-related-card strong {
  margin: 7px 0;
  font-size: 14px;
  line-height: 1.45;
}
.hub-related-card p {
  margin: 0;
  padding: 0 14px;
  color: #666;
  font-size: 12px;
  line-height: 1.55;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}
/* 测评：2×2 提示词 + 2 列步骤 */
.hub-cat-reviews .hub-creating-steps-inline {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
/* 替代方案：4 列对比 + 紧凑提示词 */
.hub-cat-alternatives .hub-creating-steps-inline {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.hub-cat-alternatives .hub-inline-links {
  background: var(--hub-accent-soft);
  border-color: var(--hub-accent-border);
}
.hub-cat-alternatives .hub-inline-links a {
  border-color: var(--hub-accent-border);
  color: var(--hub-accent);
}
.hub-cat-alternatives .hub-table-block .hub-theme-box {
  border-style: dashed;
}
/* 操作方法：纵向步骤 + 大图示例 */
.hub-cat-how-to-guides .hub-creating-steps-inline {
  grid-template-columns: 1fr;
}
.hub-cat-how-to-guides .hub-creating-step {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 0 14px;
  align-items: start;
}
.hub-cat-how-to-guides .hub-creating-step > span {
  grid-row: 1 / 3;
  margin-bottom: 0;
}
/* 提示词指南：突出 Prompt 模块 */
.hub-cat-prompt-guides .hub-creating-steps-inline {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hub-cat-prompt-guides .hub-prompt-text-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.hub-cat-prompt-guides .hub-prompt-text-card {
  margin: 0;
}
.hub-side-box {
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.hub-side-box h2 {
  margin: 0 0 12px;
  font-size: 15px;
}
.hub-side-group-label {
  margin: 12px 0 4px;
  padding: 0 2px;
  color: #8b8fa3;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.hub-side-group-label:first-of-type {
  margin-top: 0;
}
.hub-side-features a,
.hub-side-reads a {
  display: block;
  padding: 9px 8px;
  border-top: 1px solid #f0f1f3;
  border-radius: 6px;
  font-size: 13px;
  color: #555;
  line-height: 1.45;
}
.hub-side-features a:first-of-type,
.hub-side-reads a:first-of-type {
  border-top: none;
}
.hub-side-features a:hover,
.hub-side-reads a:hover {
  color: #059669;
  background: rgba(5, 150, 105, 0.05);
}
.hub-side-reads a strong {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  font-size: 13px;
  line-height: 1.45;
}
.hub-side-reads a span {
  display: block;
  margin-top: 3px;
  color: #8b8fa3;
  font-size: 12px;
}
.hub-article-sidebar {
  min-width: 0;
}
.hub-sticky {
  position: sticky;
  top: 18px;
  display: grid;
  gap: 14px;
  align-self: start;
}
.hub-toc,
.hub-side-cta {
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.hub-toc h2 {
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f1f3;
  font-size: 15px;
}
.hub-toc-scroll {
  padding-top: 4px;
  padding-right: 2px;
}
.hub-toc-scroll a {
  display: block;
  padding: 9px 8px;
  border-top: 1px solid #f0f1f3;
  color: #555;
  font-size: 13px;
  line-height: 1.45;
  border-radius: 4px;
  transition: color 0.15s ease, background 0.15s ease;
}
.hub-toc-scroll a:first-child {
  border-top: none;
}
.hub-toc-scroll a:hover {
  color: #059669;
}
.hub-toc-scroll a.is-active {
  color: #059669;
  font-weight: 700;
  background: rgba(5, 150, 105, 0.08);
  border-top-color: transparent;
}
.hub-toc-scroll a.is-active + a {
  border-top-color: transparent;
}
.hub-side-cta {
  background: rgba(5, 150, 105, 0.07);
  border: 1px solid rgba(5, 150, 105, 0.2);
  border-left: 3px solid #059669;
  color: #1a1a2e;
}
.hub-side-cta span {
  display: block;
  margin-bottom: 8px;
  color: #059669;
  font-size: 12px;
  font-weight: 800;
}
.hub-side-cta h2 {
  margin: 0 0 8px;
  font-size: 17px;
  color: #1a1a2e;
}
.hub-side-cta p {
  margin: 0 0 14px;
  color: #666;
  font-size: 13px;
  line-height: 1.6;
}
.hub-side-cta a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 38px;
  padding: 9px 12px;
  border-radius: 8px;
  background: #059669;
  color: #fff;
  font-weight: 800;
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
}
.hub-not-found {
  padding: 60px 24px;
  text-align: center;
}
@media (max-width: 1040px) {
  .hub-article-layout {
    grid-template-columns: 1fr;
  }
  .hub-sticky {
    position: static;
  }
}
@media (max-width: 720px) {
  .hub-detail-inner {
    padding: 24px 14px 62px;
  }
  .hub-article-header {
    padding: 18px;
  }
  .hub-article-body {
    padding: 20px 18px 24px;
  }
  .hub-article-section,
  .hub-related-section {
    margin-top: 28px;
    padding-top: 28px;
  }
  .hub-main-feature {
    margin-top: 28px;
    padding: 20px 18px;
  }
  .hub-main-feature-cta h2 {
    font-size: 22px;
  }
  .hub-main-feature-grid {
    grid-template-columns: 1fr;
  }
  .hub-article-header h1 {
    font-size: 28px;
  }
  .hub-in-article-nav {
    margin-bottom: 22px;
  }
  .hub-in-article-toggle {
    padding: 12px 14px;
  }
  .hub-in-article-scroll {
    max-height: 220px;
    padding: 10px 14px 12px;
  }
  .hub-media-cols-2,
  .hub-media-cols-4 {
    grid-template-columns: 1fr;
  }
  .hub-cat-prompt-guides .hub-prompt-text-list {
    grid-template-columns: 1fr;
  }
  .hub-cat-alternatives .hub-creating-steps-inline {
    grid-template-columns: 1fr;
  }
  .hub-pros-cons-table th,
  .hub-pros-cons-table td {
    display: block;
    width: 100%;
    border-right: none;
  }
  .hub-pros-cons-table td:first-child {
    border-bottom: none;
    font-weight: 700;
    background: rgba(5, 150, 105, 0.04);
  }
  .hub-creating-steps-inline {
    grid-template-columns: 1fr;
  }
  .hub-related-grid,
  .hub-navigation-grid,
  .hub-creating-steps,
  .hub-creating-steps-review,
  .hub-pros-cons-grid {
    grid-template-columns: 1fr;
  }
}
</style>
